import React from 'react';
import Typography from '@mui/material/Typography';
import useStore from 'utils/ieltsStore';
import { Box } from '@mui/material';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import { makeStyles } from '@mui/styles';

const useStyle = makeStyles(theme=>({
  timer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
}));

function Timer({ hide, totalTime, onTimeout, warningPercentange = 10 }) {
  const classes = useStyle();
  const streamDuration = React.useRef(0);
  const previousTime = React.useRef(0);
  const requestAnimationFrameId = React.useRef(null);

  const timeLeft = useStore(state => state.timeLeft);
  const playing = useStore(state => state.playing);
  const stopped = useStore(state => state.stopped);
  const section = useStore(state => state.section);

  const start = useStore(state => state.start);
  const setTimeLeft = useStore(state => state.setTimeLeft);
  const stop = useStore(state => state.stop);

  const [warning, setWarning] = React.useState(false);

  const updateTimer = React.useCallback(() => {
    let now = performance.now();
    let dt = now - previousTime.current;

    let _timeLeft;
    if (dt >= 1000) {
      streamDuration.current = streamDuration.current + Math.round(dt / 1000);


      _timeLeft = totalTime - streamDuration.current;

      setTimeLeft(_timeLeft);

      previousTime.current = now;

      if (_timeLeft < 0) return;
    }
    requestAnimationFrameId.current = requestAnimationFrame(updateTimer);
  }, [totalTime, warningPercentange]);

  const startTimer = React.useCallback(() => {
    if (requestAnimationFrameId.current) {
      cancelAnimationFrame(requestAnimationFrameId.current);
      requestAnimationFrameId.current = 0;
    }
    previousTime.current = performance.now();
    requestAnimationFrameId.current = requestAnimationFrame(updateTimer);
  }, [updateTimer]);

  const startHandler = () => {
    streamDuration.current = 0;
    setWarning(false);
    startTimer();
  };

  const pauseHandler = () => {
    cancelAnimationFrame(requestAnimationFrameId.current);
  }

  const stopHandler = React.useCallback(() => {
    setWarning(false);
    cancelAnimationFrame(requestAnimationFrameId.current);
  }, []);


  const resumeHandler = () => {
    startTimer();
  };

  React.useEffect(() => {
    if (section >= 0) stopHandler();
  }, [section]);

  React.useEffect(() => {
    start(totalTime)
    startHandler();

    return () => stopHandler();
  }, [totalTime]);

  React.useEffect(() => {
    if (playing) resumeHandler();
    else pauseHandler();
  }, [playing]);

  React.useEffect(() => {
    if (playing && !stopped) {
      setWarning(Math.round((timeLeft / totalTime) * 100) <= warningPercentange);
      if (timeLeft <= 0) {
        stop();
        stopHandler();
        onTimeout();
      }
    }
  }, [timeLeft, playing, stopped, totalTime]);

  React.useEffect(() => {
    if (stopped && timeLeft > 0) stopHandler();
  }, [stopped, timeLeft, stopHandler]);

  return hide ? null : (
    <Box className={classes.timer}>
      <AccessAlarmIcon/>
      <Typography variant='body01-bold'>
        {new Date((timeLeft) * 1000).toISOString().substring(11, 19)} Remaining
      </Typography>
    </Box>
  );
}

export default Timer;